import { useEffect, useMemo, useState } from 'react';
import {
  fetchStaffOnlineStatuses,
  getCachedStaffOnlineStatuses,
  type StaffOnlineStatusRow,
} from '@/lib/staff-online-status';

/** Carrega presença online em lote via proxy interno `/xat/onlinecheck`. */
export function useStaffOnlineStatuses(ids: string[]): {
  byId: Record<string, StaffOnlineStatusRow>;
  loading: boolean;
} {
  const idsList = useMemo(() => {
    return [
      ...new Set(
        ids
          .map((id) => String(id ?? '').trim().toLowerCase())
          .filter((id) => /^[a-z0-9_-]{1,64}$/.test(id))
      ),
    ].sort();
  }, [ids]);

  const key = idsList.join(',');

  const [byId, setById] = useState<Record<string, StaffOnlineStatusRow>>(() =>
    idsList.length ? getCachedStaffOnlineStatuses(idsList) : {}
  );
  const [loading, setLoading] = useState(() => {
    if (!idsList.length) return false;
    const cached = getCachedStaffOnlineStatuses(idsList);
    return Object.keys(cached).length < idsList.length;
  });

  useEffect(() => {
    if (!key) {
      setById({});
      setLoading(false);
      return;
    }

    const list = key.split(',');
    const controller = new AbortController();

    const cached = getCachedStaffOnlineStatuses(list);
    setById(cached);
    const hasAllCached = list.every((id) => cached[id]);
    setLoading(!hasAllCached);

    void fetchStaffOnlineStatuses(list, controller.signal, (partial) => {
      if (controller.signal.aborted) return;
      setById((prev) => ({ ...prev, ...partial }));
    }).then((map) => {
      if (controller.signal.aborted) return;
      const merged: Record<string, StaffOnlineStatusRow> = {};
      for (const id of list) {
        merged[id] = map[id] ?? { status: 'offline', online: false, presence: 'offline' };
      }
      setById(merged);
      setLoading(false);
    });

    return () => controller.abort();
  }, [key]);

  return { byId, loading };
}
